import React, { useEffect, useRef, useState } from 'react';
import {
  subscribeWeb,
  webLoadingFalse,
  webLoadingTrue,
  webNavigate,
} from '../../services/navigate.service';
import SearchIcon from '../../images/SearchIcon';
import PlusIcon from '../../images/PlusIcon';
import TableReport from '../../components/TableReport';
import CloseIcon from '../../images/CloseIcon';
import {
  createProduct,
  deleteGroup,
  deleteProduct,
  getGroupProduct,
  getListProductDashboard,
  getQuickAddDefaults,
  updateProduct,
  updateStatusProduct,
} from '../../services/items.services';
import ChangePriceDialog from './ChangePriceDialog';
import TableReportMobile from '../../components/TableReportMobile';
import { extractValues, updateUrlParams } from '../../validation/ValidationInput';
import PlusIconWhite from '../../images/PlusIconWhite';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import { showToast, showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import ItmesIcon from '../../images/ItemsIcon';
import QuickAddItemDialog from './QuickAddItemDialog';
import { useResponsive } from '../../hook/useResponsive';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import CurrencyIcon from '../../images/CurrencyIcon';
import DuplicateIcon from '../../images/DuplicateIcon';
import {
  BORDER_SOFT,
  BottomSheet,
  Density,
  ListBulkAction,
  ListColumn,
  ListFilter,
  ListFilterBar,
  ListMobile,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  ListTable,
  PAGE_SIZES,
  Pill,
  SheetRow,
  TagChip,
  cells,
  formatDayMonth,
  formatMoney,
  formatUpdatedAt,
} from '../../components/list';
import BulkSetPriceDialog from './BulkSetPriceDialog';
import BulkDeleteDialog from './BulkDeleteDialog';
import {
  Icon,
  SORT_OPTIONS,
  STATUS_OPTIONS,
  TEAL,
  TEXT,
  TEXT_DIM,
  TYPE_OPTIONS,
  currentPriceOf,
  currentPriceRowOf,
  salesAttributesOf,
  t,
  typeLabel,
} from './itemsListShared';

/**
 * Reads the back-office permission set written by dev-core-web's
 * DashboardLayout onto `window.modulesContext`. Used to decide
 * whether the "Quick Add" pill is rendered on the Items toolbar.
 *
 * Returns false (hide the button) if modules haven't loaded yet,
 * if no `Quick Add Items` row is granted, or if the permission
 * shape isn't what we expect. This is the same shape ProtectedRoute
 * in dev-core-web walks.
 */
const hasQuickAddItemsPermission = (): boolean => {
  try {
    const modules: any[] =
      (window as any)?.modulesContext?.modulesContext ?? [];
    return modules.some(
      (section: any) =>
        Array.isArray(section?.value) &&
        section.value.some(
          (v: any) => v?.name === 'Quick Add Items' && v?.value === true,
        ),
    );
  } catch {
    return false;
  }
};

declare global {
  interface Window {
    languageContext: any;
  }
}

interface InforTable {
  title: string;
  path: string;
  desc: string;
  titleCreate: string;
}
interface ProductGroupResponse {
  data: {
    productGroupWithCompany: {
      id: string;
      groupCode: string;
      groupName: string;
      country: { countryName: string };
      displayName: string;
      manager: string;
      phoneNumber: string;
      isActive: string;
    }[];
    count: number;
  };
}
type GroupRow = ProductGroupResponse['data']['productGroupWithCompany'][number];

/** ?tab=Groups opens the Groups tab; anything else is Items. */
const tabFromUrl = (): string => {
  const params: any = extractValues(location.search.split('&'));
  return params.tab === 'Groups' ? 'Groups' : 'Items';
};
interface ProductResponse {
  data: {
    productWithCompany: {
      id: string;
      sku: string;
      type: number;
      productName: string;
      productGroup: { groupName: string };
      updatedAt: Date;
      isActive: boolean;
    }[];
    count: number;
  };
}
const Items = () => {
  const { isMobile } = useResponsive();
  const preLanguage = localStorage.getItem('language');
  // Product type labels now live in itemsListShared (TYPE_OPTIONS) — the
  // Items list renders raw product records instead of flattened rows.
  const initalFilter = {
    page: 1,
    limit: 50,
    // The single free-text box on the Items tab (name / SKU / barcode). Named
    // searchValue because that is the key updateUrlParams writes as ?search=.
    searchValue: '',
    sort: 'DESC',
    tab: 'Items',
    // Items tab filter chips.
    searchType: '',
    searchStatus: '',
    searchGroup: '',
    searchTime: '',
    // Items tab sort. Never 'price': an item's price lives in a history table
    // with validity windows, so there is no single sortable value.
    sortBy: 'updated',
    sortDir: 'DESC' as 'ASC' | 'DESC',
  };

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [idDelete, setIdDelete] = useState<string>('');
  const [language, setLanguage] = useState<any>(null);
  const [checkRecall, setCheckReCall] = useState<boolean>(false);
  // Read before the first render so the Groups list mounts once, not after an Items flash.
  const [tab, setTab] = useState<string>(() => tabFromUrl());
  const [listProduct, setListProduct] = useState<any>([]);
  /** The Groups tab is its own ListScreen; its count feeds the title row. */
  const groupListRef = useRef<ListScreenHandle>(null);
  const [groupCount, setGroupCount] = useState<number>(0);
  const [formValues, setFormValues] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  // The duplicate dialog saves through the item form.
  const limits = useFieldLimits(inventoryFormLimits('item'));
  const [loading, setLoading] = useState<boolean>(false);
  const companyId: string = localStorage.getItem('companyId') || '';
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [showDuplicate, setShowDuplicate] = useState<boolean>(false);
  const [showQuickAdd, setShowQuickAdd] = useState<boolean>(false);
  // "Change Price" modal — the simple list-row flow (shared dialog).
  const [showEditPrice, setShowEditPrice] = useState<boolean>(false);
  const [editPriceProduct, setEditPriceProduct] = useState<any>(null);
  /**
   * Whether the user holds the back-office "Quick Add Items" permission
   * AND the company's country has a quick-add defaults row. The pill is
   * hidden entirely when either is false so users aren't teased with a
   * non-functional button. Re-checked after `language` populates so the
   * permission walk runs once modules are loaded by the host shell.
   */
  const [canQuickAdd, setCanQuickAdd] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>(initalFilter);

  /* ---------------- Items list: selection + bulk actions ---------------- */

  /** Ids selected on the CURRENT page. Cleared on every refetch. */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  /** Mobile only: long-press puts the card list into select mode. */
  const [selectMode, setSelectMode] = useState<boolean>(false);
  const [showBulkPrice, setShowBulkPrice] = useState<boolean>(false);
  const [showBulkDelete, setShowBulkDelete] = useState<boolean>(false);
  /** Mobile "Add an item" sheet behind the round "…" button. */
  const [showAddSheet, setShowAddSheet] = useState<boolean>(false);
  /** Group names for the Group filter chip / mobile filter sheet. */
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [density, setDensity] = useState<Density>('default');
  /** Columns the user hid through the Columns popover (absent = shown). */
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});

  const [inforTable, setInforTable] = useState<any>({
    title: 'No Items Found',
    path: '/items/',
    desc: 'Add a new item and manage your inventory or services here.',
    titleCreate: 'New Item',
  });
  const [termsArray, setTermsArray] = useState<any>({
    count: 0,
    data: [],
  });

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };
  useEffect(() => {
    setInforTable({
      title:
        language && language?.[`No ${tab} Found`]
          ? language?.[`No ${tab} Found`]
          : `No ${tab} Found`,
      path: tab === 'Items' ? '/items/' : '/groups/',
      desc:
        language && language?.['Add a new item and manage your inventory or services here.']
          ? language?.['Add a new item and manage your inventory or services here.']
          : 'Add a new item and manage your inventory or services here.',
      titleCreate:
        tab === 'Items'
          ? language && language?.['New Items']
            ? language?.['New Items']
            : 'New Items'
          : language && language?.['New Groups']
            ? language?.['New Groups']
            : 'New Groups',
      img: <ItmesIcon />
    });
  }, [filter.page, tab]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /**
   * Decide whether to render the Quick Add pill on this page. Runs once
   * on mount and probes both the permission AND the country defaults
   * endpoint — if either is missing, the button stays hidden. Doing
   * the probe up-front avoids the user clicking a button only to see
   * a "not configured" error inside the modal.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hasQuickAddItemsPermission()) {
        if (!cancelled) setCanQuickAdd(false);
        return;
      }
      try {
        await getQuickAddDefaults();
        if (!cancelled) setCanQuickAdd(true);
      } catch {
        // 404 (no country defaults) or any other failure → hide the button.
        if (!cancelled) setCanQuickAdd(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  const getListItemsAndGroupItems = async (val: any) => {
    webLoadingFalse();
    try {
      // Every refetch invalidates the selection — including a switch to the
      // Groups tab, which must not keep a hidden Items selection alive.
      setSelectedIds([]);
      setSelectMode(false);
      // The Groups tab loads through its own ListScreen.
      if (val.tab === 'Items') {
        const response: ProductResponse = await getListProductDashboard(
          val.page,
          val.limit,
          val.searchType,
          // The Items tab no longer has per-column Name / SKU inputs — one
          // `search` box covers name, SKU and barcode server-side.
          '',
          '',
          val.searchGroup,
          val.searchTime,
          val.searchStatus,
          companyId,
          val.searchValue,
          val.sortBy,
          val.sortDir
        );
        // The redesigned list renders the raw product records (it needs
        // prices and sales attributes, which the flattened row shape drops).
        setListProduct(response.data.productWithCompany);
        setTermsArray({ count: response.data.count, data: [] });
      }
    } catch (error) {
      console.error('Error loading terms:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Debounced fetch for the search box. `loading` flips on here — when the
   * request actually starts — not on the first keystroke, so the table does
   * not sit on a skeleton for the whole 700ms typing window.
   */
  useDebounceEffect(
    () => {
      if (!checkRecall) {
        setLoading(true);
        getListItemsAndGroupItems(filter);
      }
    },
    [filter],
    700
  );

  /**
   * Push the whole list state into the query string so Back, refresh and a
   * shared link all restore the exact same list. Every Items-tab filter and
   * the sort live here alongside search / page / limit; updateUrlParams
   * percent-encodes, extractValues decodes, so "Coca Cola" round-trips.
   */
  const urlFor = (next: any) =>
    updateUrlParams(
      next,
      next.tab === 'Items'
        ? {
          type: next.searchType,
          status: next.searchStatus,
          group: next.searchGroup,
          updated: next.searchTime,
          sortBy: next.sortBy,
          sortDir: next.sortDir,
        }
        : undefined
    );

  /** One history entry per deliberate action: chip, sort, page, limit, tab. */
  const pushUrl = (next: any) => {
    webNavigate(urlFor(next));
  };

  /**
   * Search typing: webNavigate → RouteManager → navigate() PUSHES, so pushing
   * on every keystroke made Back step through each partial query. Replace the
   * current entry in place instead; refresh and share-link still restore it.
   */
  const replaceUrl = (next: any) => {
    window.history.replaceState(window.history.state, '', location.pathname + urlFor(next));
  };

  /** The list state the current URL encodes (mount, Back and Forward). */
  const filterFromUrl = (): any => {
    const filterParams: any = extractValues(location.search.split('&'));
    // Only the sizes the page-size picker offers: ?limit=300 would otherwise
    // let select-all push past the API's 200-id cap on the bulk endpoints.
    const parsedLimit = +filterParams.limit;
    const limitFromUrl = PAGE_SIZES.includes(parsedLimit) ? parsedLimit : undefined;
    return {
      ...initalFilter,
      tab: tabFromUrl(),
      searchValue: filterParams.search ? filterParams.search : initalFilter.searchValue,
      page: filterParams.page ? +filterParams.page : initalFilter.page,
      limit: limitFromUrl ?? initalFilter.limit,
      searchType: filterParams.type ?? '',
      searchStatus: filterParams.status ?? '',
      searchGroup: filterParams.group ?? '',
      searchTime: filterParams.updated ?? '',
      sortBy: filterParams.sortBy || initalFilter.sortBy,
      sortDir: filterParams.sortDir === 'ASC' ? 'ASC' : 'DESC',
    };
  };

  useEffect(() => {
    const data: any = filterFromUrl();
    setFilter((prevFilter: any) => ({
      ...prevFilter,
      tab: data.tab,
      searchValue: data.searchValue,
      page: data.page || prevFilter.page,
      limit: data.limit || prevFilter.limit,
      searchType: data.searchType,
      searchStatus: data.searchStatus,
      searchGroup: data.searchGroup,
      searchTime: data.searchTime,
      sortBy: data.sortBy,
      sortDir: data.sortDir,
    }));
    setTab(data.tab);

    const handleNewToken = () => {
      getListItemsAndGroupItems(data);
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, []);

  // Tab, chip and page changes push entries: Back / Forward restore that entry's tab and list.
  useEffect(() => {
    const pagePath = location.pathname;
    const handlePopState = () => {
      // Leaving the page fires popstate too; only this page's own entries are restored.
      if (location.pathname !== pagePath) return;
      const next = filterFromUrl();
      setTab(next.tab);
      setFilter(next);
      setCheckReCall(true);
      setLoading(true);
      getListItemsAndGroupItems(next);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /**
   * Group names behind the "Group" filter. Fetched once — a company's group
   * list is short and static compared with the item list.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response: any = await getGroupProduct(companyId, 1, 200, '');
        const rows: any[] =
          response?.data?.productGroupWithCompany ??
          response?.data?.data?.productGroupWithCompany ??
          [];
        if (!cancelled) {
          setGroupOptions(
            rows.map((row: any) => String(row?.groupName ?? '').trim()).filter((n: string) => n !== '')
          );
        }
      } catch {
        // A missing group list only costs the Group filter its options.
        if (!cancelled) setGroupOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Apply a new list state: refetch immediately and mirror it into the URL. */
  const applyFilter = (updatedFilter: any) => {
    setFilter(updatedFilter);
    setCheckReCall(true);
    setLoading(true);
    pushUrl(updatedFilter);
    getListItemsAndGroupItems(updatedFilter);
  };

  const handleReceivePageNumber = (pageNumber: number) => {
    applyFilter({ ...filter, page: pageNumber, limit: filter.limit || 50 });
  };

  const handleChangeLimit = (limit: number) => {
    applyFilter({ ...filter, limit, page: 1 });
  };

  /**
   * The single search box. Debounced (700ms) through useDebounceEffect rather
   * than fetched immediately, so typing does not fire a request per keystroke.
   */
  const handleChangeSearchValue = (value: string) => {
    setCheckReCall(false);
    const updatedFilter = { ...filter, searchValue: value, page: 1 };
    setFilter(updatedFilter);
    replaceUrl(updatedFilter);
  };

  const handleChangeFilterChip = (key: string, value: string) => {
    applyFilter({ ...filter, [key]: value, page: 1 });
  };

  const handleClearFilterChips = () => {
    applyFilter({
      ...filter,
      searchType: '',
      searchStatus: '',
      searchGroup: '',
      searchTime: '',
      page: 1,
    });
  };

  const handleChangeSort = (sortBy: string, sortDir: 'ASC' | 'DESC') => {
    applyFilter({ ...filter, sortBy, sortDir, page: 1 });
  };

  const handleToggleColumn = (key: string) => {
    setVisibleColumns(prev => ({ ...prev, [key]: prev[key] === false }));
  };

  const toggleRowSelection = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  /** Header checkbox: select or clear every row on the CURRENT page. */
  const togglePageSelection = () => {
    const pageIds = listProduct.map((p: any) => String(p.id));
    const allSelected = pageIds.length > 0 && pageIds.every((id: string) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : pageIds);
  };

  const selectedProducts = listProduct.filter((p: any) =>
    selectedIds.includes(String(p.id))
  );

  const goToItem = (id: string) => webNavigate('/' + preLanguage + '/items/' + id);

  /**
   * Mobile record sheet: flip an item between Active and Inactive, then
   * refetch so the card and its pill reflect the new state.
   */
  const handleToggleItemStatus = async (id: string) => {
    const product = listProduct.find((p: any) => String(p.id) === String(id));
    if (!product) return;
    setLoading(true);
    try {
      await updateStatusProduct(id, { isActive: !product.isActive });
    } catch (error: any) {
      showApiError(error, {
        language,
        titleFallback: language?.['Status update failed.'] ?? 'Status update failed.',
      });
    } finally {
      getListItemsAndGroupItems(filter);
    }
  };

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };

  const handleShowDuplicate = (id: string) => {
    setShowDuplicate(true);
    setIdDelete(id);
  };

  const handleShowEditPrice = (id: string) => {
    const product = listProduct.find((p: any) => String(p.id) === String(id));
    if (!product) return;
    setEditPriceProduct(product);
    setShowEditPrice(true);
  };


  const handleDeleteEmployee = async () => {
    const isGroup = filter.tab === 'Groups';
    try {
      if (!isGroup) {
        const response = await deleteProduct(idDelete);
        showDeleteToast(language?.['Item Deleted !'] ?? 'Item Deleted !');
      } else {
        const response = await deleteGroup(idDelete);
        showDeleteToast(language?.['Group Deleted !'] ?? 'Group Deleted !');
      }
    } catch (error) {
      showApiError(error, { language });
    } finally {
      if (isGroup) groupListRef.current?.reload();
      else getListItemsAndGroupItems(filter);
      setShowDelete(false);
    }
  };

  const handleChangeTab = (value: string) => {
    // The Groups ListScreen only reads the URL on mount, so a re-click must not reset the URL under it.
    if (value === 'Groups' && tab === 'Groups') return;
    setTab(value);
    // Each tab has its own filters; carrying one tab's searches into the
    // other silently returned an empty list.
    const updatedFilter = {
      ...filter,
      ...initalFilter,
      tab: value,
      sortBy: filter.sortBy,
      sortDir: filter.sortDir,
    };
    applyFilter(updatedFilter);
  };

  /**
   * Duplicate (clone) an existing product. The dialog collects four
   * required fields from the user — SKU, Item Name, Display Name, and
   * the new Price — and copies everything else from the source product.
   * The new price overrides EVERY row in the source's productPrices
   * array (cost / validity window / tax stay untouched); admins can
   * tweak per-row prices after the clone via the standard Items > Edit
   * flow.
   */
  const handleDuplicateItem = async () => {
    const newErrors: any = validateAgainstLimits(formValues, limits, language, ['sku', 'productName', 'displayName']);
    if (!formValues.productName?.trim()) {
      newErrors.productName = language?.['Product Name is required'] ?? 'Product Name is required';
    }
    // SKU is optional on every create flow: left blank, the API allocates the
    // next QA-NNNNN for the company (same as the item form and Quick Add).
    if (!formValues.displayName?.trim()) {
      newErrors.displayName = language?.['Display Name is required'] ?? 'Display Name is required';
    }
    const priceStr = String(formValues.price ?? '').trim();
    const priceNum = Number(priceStr);
    if (priceStr === '' || !Number.isFinite(priceNum) || priceNum < 0) {
      newErrors.price = language?.['Price is required'] ?? 'Price is required';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const productSelect = listProduct.find((v: any) => v.id === idDelete);
    if (!productSelect) {
      showToast({
        message: language?.['Failed to duplicate item'] ?? 'Failed to duplicate item',
        desc: `<p class='font-inter text-[14px] text-main leading-6'>${
          language?.['Source product not found'] ?? 'Source product not found'
        }</p>`,
        type: 'error',
        duration: 6000,
      });
      return;
    }

    // Base UOM is required by the create endpoint. If the source product
    // somehow has no UOMs or none flagged isBase, the clone can't proceed —
    // tell the user instead of crashing with a TypeError.
    const baseUOM = productSelect.productUOMs?.find?.((v: any) => v.isBase);
    if (!baseUOM?.UOMId) {
      showToast({
        message: language?.['Failed to duplicate item'] ?? 'Failed to duplicate item',
        desc: `<p class='font-inter text-[14px] text-main leading-6'>${
          language?.['Source product has no base UOM'] ?? 'Source product has no base UOM'
        }</p>`,
        type: 'error',
        duration: 6000,
      });
      return;
    }

    // Whitelist the fields the create endpoint cares about — spreading
    // the whole entity (createdAt, barcodes etc.) caused conflicts and silent
    // backend errors. `id` IS sent on purpose: it is what routes the request
    // to the server-side clone (duplicateProduct), which copies everything the
    // dialog does not override — course, income/expense codes, configuration
    // incl. purchase tax + print destinations, recipe, sales attributes and
    // live allergens — from the source. Without it the API treats this as a
    // plain create and all of that is lost.
    const overriddenPrices = (productSelect.productPrices ?? []).map((p: any) => ({
      // Strip server-managed identity so each row is created fresh; keep
      // cost / validity window / tax group / price type.
      cost: p.cost,
      validFrom: p.validFrom,
      validTo: p.validTo,
      taxGroupId: p.taxGroupId,
      priceType: p.priceType,
      // Override: every row gets the price the user typed in the dialog.
      price: priceNum,
    }));

    const payload: any = {
      // Source product to clone from (see comment above).
      id: productSelect.id,
      // Identity overrides from the dialog. A blank SKU is OMITTED so the API
      // auto-generates one; sending '' would work too but absent is the
      // documented contract.
      ...(formValues.sku?.trim() ? { sku: formValues.sku.trim() } : {}),
      productName: formValues.productName.trim(),
      displayName: formValues.displayName.trim(),
      // Copy semantically meaningful fields off the source
      type: productSelect.type,
      productGroupId: productSelect.productGroupId,
      assetCategoryId: productSelect.assetCategoryId ?? null,
      // Not sent: courseId, attributes, allergens, recipe, configuration —
      // the API copies them from the source (the list row's `course` object is
      // not a column and must not be sent).
      maxDiscount: productSelect.maxDiscount ?? null,
      image: productSelect.image ?? null,
      isActive: productSelect.isActive ?? true,
      // Pricing
      productPrice: overriddenPrices,
      // UOMs
      baseUOMId: baseUOM.UOMId,
      alternativeIds: (productSelect.productUOMs ?? [])
        .filter((v: any) => !v.isBase)
        .map((v: any) => ({
          allowOrdering: v.allowOrdering,
          allowRecipe: v.allowRecipe,
          UOMId: v.UOMId,
        })),
    };

    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));

    setLoading(true);
    try {
      await createProduct(formData);
      showToast({
        message: language?.['Item duplicated'] ?? 'Item duplicated',
        type: 'success',
        duration: 3000,
      });
      setShowDuplicate(false);
      setFormValues({});
      setErrors({});
      getListItemsAndGroupItems(filter);
    } catch (error: any) {
      showApiError(error, {
        language,
        titleFallback: language?.['Failed to duplicate item'] ?? 'Failed to duplicate item',
        setFieldErrors: fe => setErrors((prev: any) => ({ ...prev, ...fe })),
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Items list: kit configuration ---------------- */

  /**
   * The Items columns. Sorting is opt-in per column and maps onto the
   * sortBy keys list-for-dashboard accepts; the mobile card shows name over
   * "SKU · Group" with the price and status pill trailing.
   */
  const itemColumns: ListColumn[] = [
    {
      key: 'item',
      label: 'Item',
      width: 250,
      sortable: true,
      sortKey: 'name',
      hideable: false,
      render: cells.primary((p: any) => p.productName, (p: any) => p.sku),
      mobile: 'title',
      mobileRender: (p: any) => p.productName,
      onClick: (p: any) => goToItem(String(p.id)),
    },
    { key: 'sku', label: 'SKU', width: 0, desktop: false, mobile: 'subtitle', value: (p: any) => p.sku },
    {
      key: 'group',
      label: 'Group',
      width: 120,
      sortable: true,
      value: (p: any) => p.productGroup?.groupName,
      mobile: 'subtitle',
    },
    {
      key: 'type',
      label: 'Type',
      width: 104,
      sortable: true,
      render: cells.typePill((p: any, ctx) => typeLabel(p.type, ctx.language)),
      mobile: 'hidden',
    },
    {
      key: 'price',
      label: 'Price',
      width: 84,
      render: cells.money(currentPriceOf),
      mobileRender: cells.money(currentPriceOf, true),
      mobile: 'trailing',
    },
    {
      key: 'attributes',
      label: 'Sales attributes',
      width: 168,
      render: cells.chips(salesAttributesOf),
      mobile: 'hidden',
    },
    {
      key: 'status',
      label: 'Status',
      width: 88,
      sortable: true,
      render: cells.status((p: any) => !!p.isActive),
      mobile: 'trailing',
    },
    {
      key: 'updated',
      label: 'Last updated',
      width: 130,
      sortable: true,
      render: cells.date((p: any) => p.updatedAt),
      mobile: 'hidden',
    },
  ];

  const itemFilters: ListFilter[] = [
    {
      key: 'searchType',
      label: 'Type',
      kind: 'select',
      options: TYPE_OPTIONS.map(o => ({ value: o.value, label: t(language, o.label, o.label) })),
    },
    {
      key: 'searchStatus',
      label: 'Status',
      kind: 'select',
      options: STATUS_OPTIONS.map(o => ({ value: o, label: t(language, o, o) })),
    },
    {
      key: 'searchGroup',
      label: 'Group',
      kind: 'select',
      options: groupOptions.map(o => ({ value: o, label: o })),
    },
    { key: 'searchTime', label: 'Last update', kind: 'date' },
  ];

  /** A search or chip is active, so an empty result is "no matches", not "nothing yet". */
  const itemsHasActiveQuery =
    String(filter.searchValue ?? '').trim() !== '' ||
    !!filter.searchType || !!filter.searchStatus || !!filter.searchGroup || !!filter.searchTime;
  const itemFilterValues = {
    searchType: filter.searchType,
    searchStatus: filter.searchStatus,
    searchGroup: filter.searchGroup,
    searchTime: filter.searchTime,
  };

  const bulkActions: ListBulkAction[] = [
    { key: 'price', label: 'Set price', onRun: () => setShowBulkPrice(true) },
    { key: 'delete', label: 'Delete', onRun: () => setShowBulkDelete(true) },
  ];

  /** Desktop kebab menu. */
  const rowActions: ListRowAction[] = [
    { key: 'edit', label: 'Edit', icon: <EditIconV2 />, onRun: (p: any) => goToItem(String(p.id)) },
    { key: 'delete', label: 'Delete', icon: <DeleteIconV2 />, onRun: (p: any) => handleShowDelete(String(p.id)) },
    { key: 'price', label: 'Edit price', icon: <CurrencyIcon />, onRun: (p: any) => handleShowEditPrice(String(p.id)) },
    { key: 'duplicate', label: 'Duplicate', icon: <DuplicateIcon />, onRun: (p: any) => handleShowDuplicate(String(p.id)) },
  ];

  /** Mobile record sheet. */
  const mobileRowActions: ListRowAction[] = [
    {
      key: 'price',
      label: 'Set price',
      sheetIcon: 'sales',
      sub: 'Change price · schedule for a date',
      onRun: (p: any) => handleShowEditPrice(String(p.id)),
    },
    {
      key: 'edit',
      label: 'Edit item',
      sheetIcon: 'inventory',
      sub: 'Full form — recipe, attributes, prices',
      onRun: (p: any) => goToItem(String(p.id)),
    },
    { key: 'duplicate', label: 'Duplicate', sheetIcon: 'columns', onRun: (p: any) => handleShowDuplicate(String(p.id)) },
    {
      key: 'status',
      label: (p: any) =>
        p.isActive ? t(language, 'Set inactive', 'Set inactive') : t(language, 'Set active', 'Set active'),
      sheetIcon: 'close',
      sub: 'Hides it on the tills after the next sync',
      onRun: (p: any) => handleToggleItemStatus(String(p.id)),
    },
    { key: 'delete', label: 'Delete', sheetIcon: 'close', danger: true, onRun: (p: any) => handleShowDelete(String(p.id)) },
  ];

  /** The mobile record sheet header: name, "SKU · Group · Type", status, price and attributes. */
  const recordHeader = (record: any) => (
    <div style={{ padding: '6px 20px 14px', borderBottom: `1px solid ${BORDER_SOFT}` }}>
      <div className="flex items-start" style={{ gap: '10px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="font-inter" style={{ fontSize: '18px', fontWeight: 600, color: TEXT }}>
            {record.productName}
          </div>
          <div className="font-inter" style={{ fontSize: '13px', color: TEXT_DIM, marginTop: '2px' }}>
            {record.sku} · {record.productGroup?.groupName} · {typeLabel(record.type, language)}
          </div>
        </div>
        <Pill
          label={
            record.isActive ? t(language, 'Active', 'Active') : t(language, 'Inactive', 'Inactive')
          }
          kind={record.isActive ? 'ok' : 'off'}
        />
      </div>
      <div className="flex items-baseline" style={{ gap: '10px', marginTop: '12px' }}>
        <span
          className="font-inter"
          style={{ fontSize: '24px', fontWeight: 600, color: TEXT, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatMoney(currentPriceOf(record))}
        </span>
        <span className="font-inter" style={{ fontSize: '12.5px', color: TEXT_DIM }}>
          {currentPriceRowOf(record)?.validFrom
            ? `${t(language, 'since', 'since')} ${formatDayMonth(currentPriceRowOf(record)?.validFrom)} · `
            : ''}
          {t(language, 'updated', 'updated')} {formatUpdatedAt(record.updatedAt, language)}
        </span>
      </div>
      {salesAttributesOf(record).length > 0 && (
        <div className="flex" style={{ gap: '6px', marginTop: '12px', flexWrap: 'wrap' }}>
          {salesAttributesOf(record).map((name: string, index: number) => (
            <TagChip key={`record-attr-${index}`} label={name} />
          ))}
        </div>
      )}
    </div>
  );

  /* ---------------- Groups list: kit configuration ---------------- */

  const goToGroup = (id: string) => webNavigate('/' + preLanguage + '/groups/' + id);

  /** One page of groups; /product-groups/list takes one searchValue over code, name and display name. */
  const fetchGroups = async (query: ListQuery) => {
    webLoadingFalse();
    const response: ProductGroupResponse = await getGroupProduct(
      companyId,
      query.page,
      query.limit,
      query.search.trim()
    );
    return { rows: response.data.productGroupWithCompany ?? [], count: response.data.count ?? 0 };
  };

  const groupColumns: ListColumn<GroupRow>[] = [
    {
      key: 'groupCode',
      label: 'Code',
      width: 140,
      minWidth: 90,
      hideable: false,
      value: g => g.groupCode,
      mobile: 'subtitle',
    },
    {
      key: 'groupName',
      label: 'Group Name',
      width: 260,
      minWidth: 120,
      render: cells.primary(g => g.groupName),
      mobileRender: g => g.groupName,
      mobile: 'title',
      onClick: g => goToGroup(String(g.id)),
    },
    { key: 'displayName', label: 'Display Name', width: 260, minWidth: 120, mobile: 'sheet' },
  ];

  const groupRowActions: ListRowAction<GroupRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIconV2 />,
      sheetIcon: 'inventory',
      onRun: g => goToGroup(String(g.id)),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIconV2 />,
      sheetIcon: 'close',
      danger: true,
      onRun: g => handleShowDelete(String(g.id)),
    },
  ];

  return (
    <>
      {/*
        Desktop is a flex column so the table fills whatever height is left
        after the (now single-line) title row — the old
        calc(100% - 85px - 24px) was tuned to the two-line header this design
        replaces and would leave a dead strip at the bottom.
      */}
      <div
        className="h-full relative "
        style={{
          overflowY: isMobile ? "auto" : undefined,
          paddingBottom: isMobile ? "24px" : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        {/*
          Title row — the tab name, the record count, the Items / Groups
          switch and the page CTAs on one line (Main.dc.html). On mobile the
          Items tab swaps the three desktop buttons for the primary
          "Quick add" pill plus a round "…" that opens the Add sheet
          (MobileFlows.dc.html, flow 3).
        */}
        <div className="">
          <div className="flex items-center" style={{ gap: '12px', flexWrap: 'wrap' }}>
            <span
              className="font-inter"
              style={{ fontSize: '24px', fontWeight: 500, lineHeight: '32px', color: TEXT }}
            >
              {t(language, tab, tab)}
            </span>
            <span className="font-inter" style={{ fontSize: '14px', color: TEXT_DIM }}>
              {Number((tab === 'Groups' ? groupCount : termsArray.count) ?? 0).toLocaleString()}
            </span>
            <div className="flex" style={{ gap: '8px', marginLeft: '12px' }}>
              {['Items', 'Groups'].map((name: string) => (
                <div
                  key={name}
                  style={{
                    color: tab === name ? '#FFFFFF' : TEXT,
                    padding: '6px 18px',
                    borderRadius: '30px',
                    backgroundColor: tab === name ? '#0D5759' : 'transparent',
                    border: tab === name ? '1px solid transparent' : '1px solid #C9D7D8',
                  }}
                  onClick={() => handleChangeTab(name)}
                  className="cursor-pointer text-[14px] leading-[20px] font-medium font-inter"
                >
                  {t(language, name, name)}
                </div>
              ))}
            </div>
            <div className="flex items-center" style={{ marginLeft: 'auto', gap: '12px' }}>
              {tab === 'Items' && isMobile ? (
                <>
                  {/*
                    Quick add is the primary mobile CTA. The existing
                    permission probe still gates it: without the back-office
                    "Quick Add Items" permission or a country defaults row the
                    pill falls back to "Add item" and the sheet drops the
                    Quick add row.
                  */}
                  <div
                    className="cursor-pointer flex items-center justify-center"
                    style={{
                      gap: '8px',
                      padding: '8px 24px 8px 16px',
                      backgroundColor: TEAL,
                      borderRadius: '30px',
                    }}
                    onClick={() =>
                      canQuickAdd
                        ? setShowQuickAdd(true)
                        : webNavigate('/' + preLanguage + '/items/new')
                    }
                  >
                    <Icon name="plus" color="#FFFFFF" size={18} />
                    <p
                      style={{ color: '#FFFFFF' }}
                      className="text-[14px] leading-[20px] font-medium font-inter"
                    >
                      {canQuickAdd
                        ? t(language, 'Quick add', 'Quick add')
                        : t(language, 'Add item', 'Add item')}
                    </p>
                  </div>
                  <div
                    className="cursor-pointer flex items-center justify-center"
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: '1px solid #C9D7D8',
                    }}
                    onClick={() => setShowAddSheet(true)}
                  >
                    <Icon name="more" color="#4A5353" size={18} />
                  </div>
                </>
              ) : (
                <>
                  {tab === 'Items' && (
                    <div
                      className="cursor-pointer flex items-center justify-center"
                      style={{
                        gap: '8px',
                        padding: '8px 20px',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #C9D7D8',
                        borderRadius: '30px',
                      }}
                      onClick={() => webNavigate('/' + preLanguage + '/items/import')}
                    >
                      <p
                        style={{ color: TEXT }}
                        className="text-[14px] leading-[20px] font-medium font-inter"
                      >
                        {t(language, 'Import', 'Import')}
                      </p>
                    </div>
                  )}
                  {/*
                    Quick add — rendered only when the user holds the
                    back-office "Quick Add Items" permission AND the company's
                    country has a country_quick_add_defaults row (canQuickAdd
                    probe above).
                  */}
                  {tab === 'Items' && canQuickAdd && (
                    <div
                      className="cursor-pointer flex items-center justify-center"
                      style={{
                        gap: '8px',
                        padding: '8px 20px',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #C9D7D8',
                        borderRadius: '30px',
                      }}
                      onClick={() => setShowQuickAdd(true)}
                    >
                      <p
                        style={{ color: TEXT }}
                        className="text-[14px] leading-[20px] font-medium font-inter"
                      >
                        {t(language, 'Quick add', 'Quick add')}
                      </p>
                    </div>
                  )}
                  <div
                    className="cursor-pointer flex items-center justify-center"
                    style={{
                      gap: '8px',
                      padding: '8px 24px 8px 16px',
                      backgroundColor: TEAL,
                      borderRadius: '30px',
                    }}
                    onClick={() => {
                      tab === 'Items'
                        ? webNavigate('/' + preLanguage + '/items/new')
                        : webNavigate('/' + preLanguage + '/groups/new');
                    }}
                  >
                    <div
                      style={{ width: '24px', height: '24px' }}
                      className="flex items-center justify-center"
                    >
                      <PlusIconWhite />
                    </div>
                    <p
                      style={{ color: '#FFFFFF' }}
                      className="text-[14px] leading-[20px] font-medium font-inter"
                    >
                      {tab === 'Items'
                        ? t(language, 'Add item', 'Add item')
                        : t(language, 'Add Group', 'Add Group')}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {tab === 'Groups' ? (
          <ListScreen
            ref={groupListRef}
            language={language}
            columns={groupColumns}
            fetchRows={fetchGroups}
            onLoaded={result => setGroupCount(result?.count ?? 0)}
            searchPlaceholder="Search code or name"
            searchInputRef={searchInputRef}
            extraUrlParams={{ tab: 'Groups' }}
            rowActions={groupRowActions}
            countNoun="groups"
            emptyState={inforTable}
          />
        ) : (
          <>
            <div
              style={{ marginTop: '24px', flex: 1, minHeight: 0 }}
              className="lg:block hidden"
            >
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  minHeight: 0,
                }}
              >
                <ListFilterBar
                  language={language}
                  searchValue={filter.searchValue}
                  onSearchChange={handleChangeSearchValue}
                  searchPlaceholder="Search name, SKU or barcode"
                  searchInputRef={searchInputRef}
                  filters={itemFilters}
                  filterValues={itemFilterValues}
                  onFilterChange={handleChangeFilterChip}
                  columns={itemColumns}
                  sortOptions={SORT_OPTIONS}
                  sortBy={filter.sortBy}
                  sortDir={filter.sortDir}
                  onSortChange={handleChangeSort}
                  visibleColumns={visibleColumns}
                  onToggleColumn={handleToggleColumn}
                  density={density}
                  onDensityChange={setDensity}
                />
                <div style={{ flex: 1, minHeight: 0 }}>
                  <ListTable
                    language={language}
                    columns={itemColumns}
                    rows={listProduct}
                    count={termsArray.count}
                    page={filter.page}
                    limit={filter.limit}
                    loading={loading}
                    sortBy={filter.sortBy}
                    sortDir={filter.sortDir}
                    onSortChange={handleChangeSort}
                    selection={{
                      selectedIds,
                      onToggleRow: toggleRowSelection,
                      onTogglePage: togglePageSelection,
                      onClearSelection: () => setSelectedIds([]),
                      bulkActions,
                    }}
                    rowActions={rowActions}
                    onPageChange={handleReceivePageNumber}
                    onLimitChange={handleChangeLimit}
                    visibleColumns={visibleColumns}
                    density={density}
                    emptyState={inforTable}
                    hasActiveQuery={itemsHasActiveQuery}
                  />
                </div>
              </div>
            </div>
            <div style={{ marginTop: '20px' }} className="lg:hidden block">
              <ListMobile
                language={language}
                columns={itemColumns}
                rows={listProduct}
                count={termsArray.count}
                page={filter.page}
                limit={filter.limit}
                loading={loading}
                searchValue={filter.searchValue}
                onSearchChange={handleChangeSearchValue}
                searchPlaceholder="Search name, SKU or barcode"
                filters={itemFilters}
                filterValues={itemFilterValues}
                onFilterChange={handleChangeFilterChip}
                onClearFilters={handleClearFilterChips}
                sortBy={filter.sortBy}
                sortDir={filter.sortDir}
                sortOptions={SORT_OPTIONS}
                onSortChange={handleChangeSort}
                selection={{
                  selectMode,
                  onEnterSelectMode: (id: string) => {
                    setSelectMode(true);
                    setSelectedIds([id]);
                  },
                  onExitSelectMode: () => {
                    setSelectMode(false);
                    setSelectedIds([]);
                  },
                  selectedIds,
                  onToggleRow: toggleRowSelection,
                  onSelectAllPage: togglePageSelection,
                  bulkActions,
                }}
                rowActions={mobileRowActions}
                recordHeader={recordHeader}
                onPageChange={handleReceivePageNumber}
                emptyState={inforTable}
                hasActiveQuery={itemsHasActiveQuery}
              />
            </div>
          </>
        )}
      </div>
      {/* PopupLocation was the "Please select UOM" dialog wired to a
          legacy clone-flow branch. The new handler surfaces a toast
          instead, so the popup is unreachable. Component + showPopup
          state removed. */}
      <div
        className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        style={{
          backgroundColor: '#22252552',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100vw',
          height: '100vh',
          left: 0,
          top: 0,
        }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            padding: '24px',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            width: '530px',
            maxWidth: 'calc(100vw - 32px)',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div className="flex items-center justify-between">
            <p
              style={{ fontSize: '20px', color: '#222525' }}
              className="font-semibold font-inter leading-[24px]"
            >
              {language && language?.['Delete item management?']
                ? language?.['Delete item management?']
                : 'Delete item management?'}
            </p>
            <div
              onClick={() => setShowDelete(false)}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
            <p
              className="text-[14px] font-inter leading-[24px]"
              style={{ color: '#6A7878' }}
            >
              {language &&
                language?.[
                'This item management will be permanently deleted. Are you sure you want to continue'
                ]
                ? language?.[
                'This item management will be permanently deleted. Are you sure you want to continue'
                ]
                : 'This item management will be permanently deleted. Are you sure you want to continue'}
            </p>
            {/* <span className="text-main">[sale ID]</span> sale. */}
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => setShowDelete(false)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '40px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </div>
            <div
              onClick={handleDeleteEmployee}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '40px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
            </div>
          </div>
        </div>
      </div>
      <div
        className={`fixed inset-0 flex items-center justify-center px-4 ${showDuplicate ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        style={{
          backgroundColor: '#22252552',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100vw',
          height: '100vh',
          left: 0,
          top: 0,
        }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDuplicate ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            padding: '24px',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            width: '530px',
            maxWidth: 'calc(100vw - 32px)',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p
                style={{ fontSize: '20px', color: '#222525' }}
                className="font-semibold font-inter leading-[24px]"
              >
                {language && language?.['Duplicate item?']
                  ? language?.['Duplicate item?']
                  : 'Duplicate item?'}
              </p>
              <p
                style={{ fontSize: '14px', color: '#6A7878', marginTop: '12px' }}
                className="font-inter leading-[24px]"
              >
                {language && language?.['This will copy all details of the original item?']
                  ? language?.['This will copy all details of the original item?']
                  : 'This will copy all details of the original item?'}
              </p>
            </div>
            <div
              onClick={() => {
                setShowDuplicate(false);
                setFormValues({
                  sku: '',
                  productName: '',
                  displayName: '',
                });
                setErrors({});
              }}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="" style={{ marginTop: '12px' }}>
            <div className="space-y-2">
              <div className="flex items-center">
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#4A5353' }}
                >
                  {language && language?.['SKU'] ? language?.['SKU'] : 'SKU'}
                </p>
                <p
                  className="text-[14px] font-inter leading-[20px]"
                  style={{ color: '#898989' }}
                >
                  {language && language?.['(optional, auto-generated if blank)'] ? language?.['(optional, auto-generated if blank)'] : '(optional, auto-generated if blank)'}
                </p>
              </div>
              <div className="w-full">
                <input
                  tabIndex={1}
                  value={formValues?.sku}
                  name="sku"
                  {...limitProps(limits, 'sku')}
                  placeholder={language && language?.['Leave blank to auto-generate'] ? language?.['Leave blank to auto-generate'] : 'Leave blank to auto-generate'}
                  onChange={(e: any) => {
                    handleChange(e);
                  }}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                <FieldCounter value={formValues?.sku} max={limits.sku?.maxLength} />
              </div>
              {errors.sku && <p className="error">{errors.sku}</p>}
            </div>
            <div className="space-y-2" style={{ margin: '20px 0px' }}>
              <div className="flex items-center">
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#4A5353' }}
                >
                  {language && language?.['Item Name'] ? language?.['Item Name'] : 'Item Name'}
                </p>
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#BA0E13' }}
                >
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <input
                  tabIndex={2}
                  type="text"
                  name="productName"
                  {...limitProps(limits, 'productName')}
                  value={formValues?.productName}
                  onChange={(e: any) => {
                    handleChange(e);
                  }}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                <FieldCounter value={formValues?.productName} max={limits.productName?.maxLength} />
              </div>
              {errors.productName && <p className="error">{errors.productName}</p>}
            </div>
            <div className="space-y-2">
              <div className="flex items-center">
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#4A5353' }}
                >
                  {language && language?.['Display Name']
                    ? language?.['Display Name']
                    : 'Display Name'}
                </p>
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#BA0E13' }}
                >
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <input
                  tabIndex={3}
                  type="text"
                  name="displayName"
                  {...limitProps(limits, 'displayName')}
                  value={formValues?.displayName ?? ''}
                  onChange={(e: any) => {
                    handleChange(e);
                  }}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                <FieldCounter value={formValues?.displayName} max={limits.displayName?.maxLength} />
              </div>
              {errors.displayName && <p className="error">{errors.displayName}</p>}
            </div>
            {/* Price — applies to every row of the source product's
                productPrices (overrides `price`, leaves cost / validity
                window / tax / priceType intact). Required so cloned
                items don't ship at the source's price by accident. */}
            <div className="space-y-2" style={{ margin: '20px 0px' }}>
              <div className="flex items-center">
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#4A5353' }}
                >
                  {language && language?.['Price'] ? language?.['Price'] : 'Price'}
                </p>
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#BA0E13' }}
                >
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <input
                  tabIndex={4}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  name="price"
                  value={formValues?.price ?? ''}
                  onChange={handleChange}
                  className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
                />
              </div>
              {errors.price && <p className="error">{errors.price}</p>}
            </div>
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => {
                if (loading) return;
                setShowDuplicate(false);
                setFormValues({});
                setErrors({});
              }}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '40px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
                opacity: loading ? 0.5 : 1,
                pointerEvents: loading ? 'none' : 'auto',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </div>
            <div
              onClick={() => { if (!loading) handleDuplicateItem(); }}
              className="text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '40px',
                padding: '10px 24px',
                backgroundColor: loading ? '#9CA3AF' : '#0F7679',
                color: '#FFFFFF',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading
                ? (language?.['Duplicating...'] ?? 'Duplicating...')
                : (language?.['Duplicate item'] ?? 'Duplicate item')}
            </div>
          </div>
        </div>
      </div>
      {/* Change Price — the simple flow (shared dialog). */}
      <ChangePriceDialog
        isOpen={showEditPrice}
        onClose={() => {
          setShowEditPrice(false);
          setEditPriceProduct(null);
        }}
        onSaved={() => getListItemsAndGroupItems(filter)}
        productId={editPriceProduct?.id ?? null}
        productName={editPriceProduct?.productName}
        currentPrice={currentPriceOf(editPriceProduct)}
        language={language}
      />
      {/*
        Quick Add Product modal — mounted unconditionally so the entry +
        exit transitions play. Internal state resets when isOpen flips
        to false (see QuickAddItemDialog effect).
      */}
      <QuickAddItemDialog
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onCreated={() => getListItemsAndGroupItems(filter)}
        language={language}
      />
      {/*
        Bulk actions over the current selection. Both refetch on success,
        which clears the selection and keeps the active filters (the fetch is
        driven by the same `filter` object the list is rendered from).
      */}
      <BulkSetPriceDialog
        isOpen={showBulkPrice}
        onClose={() => setShowBulkPrice(false)}
        onSaved={() => getListItemsAndGroupItems(filter)}
        products={selectedProducts}
        language={language}
        isMobile={isMobile}
      />
      <BulkDeleteDialog
        isOpen={showBulkDelete}
        onClose={() => setShowBulkDelete(false)}
        onDeleted={() => getListItemsAndGroupItems(filter)}
        products={selectedProducts}
        language={language}
        isMobile={isMobile}
      />
      {/* Mobile "Add an item" sheet behind the round "…" button. */}
      <BottomSheet
        open={showAddSheet}
        onClose={() => setShowAddSheet(false)}
        title={t(language, 'Add an item', 'Add an item')}
      >
        {canQuickAdd && (
          <SheetRow
            icon="plus"
            label={t(language, 'Quick add', 'Quick add')}
            sub={t(
              language,
              'Name, price, group — the rest is auto-filled',
              'Name, price, group — the rest is auto-filled'
            )}
            onClick={() => {
              setShowAddSheet(false);
              setShowQuickAdd(true);
            }}
          />
        )}
        <SheetRow
          icon="inventory"
          label={t(language, 'New item', 'New item')}
          sub={t(
            language,
            'Full form: recipe, attributes, price history',
            'Full form: recipe, attributes, price history'
          )}
          onClick={() => {
            setShowAddSheet(false);
            webNavigate('/' + preLanguage + '/items/new');
          }}
        />
        <SheetRow
          icon="arrowd"
          label={t(language, 'Import from file', 'Import from file')}
          sub={t(
            language,
            'XLSX, up to 1,000 rows · easier on a laptop',
            'XLSX, up to 1,000 rows · easier on a laptop'
          )}
          onClick={() => {
            setShowAddSheet(false);
            webNavigate('/' + preLanguage + '/items/import');
          }}
        />
        {canQuickAdd && (
          <div
            className="font-inter"
            style={{ padding: '10px 20px 0', fontSize: '12px', color: '#898989' }}
          >
            {t(
              language,
              'Quick add is the default action on the pill; the rest live here.',
              'Quick add is the default action on the pill; the rest live here.'
            )}
          </div>
        )}
      </BottomSheet>
    </>
  );
};

export default Items;
